import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePurchases } from '../contexts/PurchasesContext';
import { supabase } from '../api/initSupabase';
import { FontText, PrimaryButton, SEOHead } from '../components/common';
import { logErrorsWithMessage } from '../utils/errors';
import { localAnalytics } from '../utils/analytics';
import { generateBookSEO } from '../utils/seoUtils';
import { Loading } from '../components/common/Loading';
import { Header } from '../components/Header';
import { ReadingIcon } from '../components/common/ReadingIcon';

interface Author {
  id: number;
  name: string;
  avatar?: string;
}

interface Genre {
  slug: string;
  title: string;
  emoji: string;
}

interface Book {
  id: number;
  title: string;
  picture: string | null;
  description: string | null;
  chapter_count: number;
  read_count: number;
  slug: string;
  seo_title?: string;
  seo_description?: string;
  seo_alt?: string;
  seo_keywords: string[];
  book_author: Array<{
    author: Author;
  }>;
  book_genre: Array<{
    genre: Genre;
  }>;
  lastChapterRead?: number;
}

export const Book: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { bookSlug } = useParams<{ bookSlug: string }>();
  const { userId, isAuthenticated, isCampaign, isAnonymous } = useAuth();
  const { isUserPremium, coinBalance } = usePurchases();
  const [book, setBook] = useState<Book | null>(null);
  const [similarBooks, setSimilarBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchBookData = async () => {
    if (!bookSlug) return;

    try {
      localAnalytics().logEvent('BookPageLoading', {
        screen: 'Book',
        action: 'DataLoading',
        userId,
        bookSlug,
      });

      const bookQuery = supabase
        .from('book')
        .select(
          `
        id, 
        title, 
        picture, 
        description,
        read_count,
        chapter_count,
        slug,
        seo_title,
        seo_description,
        seo_alt,
        seo_keywords,
        book_author!inner(author:author_id(id, name, avatar)),
        book_genre(genre(slug, title, emoji))
      `
        )
        .eq('slug', bookSlug)
        .single();

      const progressQuery =
        isAuthenticated && userId
          ? supabase.rpc('get_books_progress')
          : Promise.resolve({ data: null, error: null });

      const [bookResult, progressResult] = await Promise.all([
        bookQuery,
        progressQuery,
      ]);

      const { data: bookData, error: bookError } = bookResult;
      if (bookError) throw bookError;

      let userProgress = null;
      const { data: progressData, error: progressError } = progressResult;
      if (!progressError && progressData) {
        const progressMap = new Map(
          progressData.map(p => [p.book_id, p.chapter_number])
        );
        userProgress = progressMap.get(bookData.id) || null;
      }

      const bookWithProgress = {
        ...bookData,
        lastChapterRead: userProgress,
      };

      setBook(bookWithProgress as Book);

      const [similarBooksResult] = await Promise.all([
        supabase.rpc('get_similar_books', {
          p_book_id: bookData.id,
        }),
      ]);

      const { data: similarBooksIds, error: similarBooksRpcError } =
        similarBooksResult;

      if (similarBooksRpcError) {
        throw similarBooksRpcError;
      }

      if (similarBooksIds && similarBooksIds.length > 0) {
        const bookIds = similarBooksIds
          .map((book: any) => book.book_id)
          .slice(0, 3);

        const { data: similarBooksData, error: similarBooksError } =
          await supabase
            .from('book')
            .select(
              `
            id, 
            title, 
            picture, 
            description,
            read_count,
            chapter_count,
            slug,
            book_author!inner(author:author_id(id, name, avatar)),
            book_genre(genre(slug, title, emoji))
          `
            )
            .in('id', bookIds);

        if (similarBooksError) {
          throw similarBooksError;
        }

        setSimilarBooks((similarBooksData as Book[]) || []);
      }

      localAnalytics().logEvent('BookPageLoaded', {
        screen: 'Book',
        action: 'DataLoaded',
        userId,
        bookId: bookData.id,
        bookTitle: bookData.title,
      });
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_load_books'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookData();
  }, [bookSlug, isAuthenticated, userId]);

  useEffect(() => {
    if (!loading && !book) {
      localAnalytics().logEvent('BookNotFoundRedirect', {
        screen: 'Book',
        action: 'RedirectTo404',
        userId,
        bookSlug,
        reason: 'book_not_found',
      });
      navigate('/404', { replace: true });
    }
  }, [navigate, userId, bookSlug, book, loading]);

  const handleReadNow = () => {
    if (!book) return;

    localAnalytics().logEvent('BookReadNowClicked', {
      screen: 'Book',
      action: 'ReadNowClicked',
      userId,
      bookId: book.id,
      bookTitle: book.title,
    });
  };

  const handleSimilarBookPress = (similarBook: Book) => {
    localAnalytics().logEvent('SimilarBookClicked', {
      screen: 'Book',
      action: 'SimilarBookClicked',
      userId,
      bookId: similarBook.id,
      bookTitle: similarBook.title,
    });
  };

  if (loading) {
    return <Loading />;
  }

  if (!book) {
    return null;
  }

  const isLargeScreen = window.innerWidth > 900;
  const mainAuthor = book.book_author[0]?.author;
  const genres = book.book_genre.map(bg => bg.genre);

  const seoData = generateBookSEO(book, t);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    },
    main: {
      padding: '20px 30px',
      maxWidth: 1200,
      margin: '0 auto',
    },
    bookContent: {
      display: 'flex',
      flexDirection: isLargeScreen ? 'row' : 'column',
      gap: 30,
      marginBottom: 60,
    },
    bookImageSection: {
      display: 'flex',
      justifyContent: 'center',
      flex: isLargeScreen ? '0 0 300px' : 'none',
    },
    bookImage: {
      width: isLargeScreen ? 300 : 250,
      height: isLargeScreen ? 420 : 350,
      objectFit: 'cover',
      objectPosition: 'top',
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    },
    bookDetails: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    bookTitle: {
      color: theme.colors.text,
      marginBottom: 12,
    },
    bookAuthor: {
      color: theme.colors.textSecondary,
      marginBottom: 16,
    },
    bookGenres: {
      display: 'flex',
      gap: 8,
      marginBottom: 20,
      flexWrap: 'wrap',
    },
    genreTag: {
      backgroundColor: theme.colors.surface,
      padding: '6px 12px',
      borderRadius: 20,
      border: `1px solid ${theme.colors.overlay}`,
    },
    genreText: {
      color: theme.colors.text,
    },
    bookStats: {
      display: 'flex',
      gap: 20,
      marginBottom: 24,
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    statText: {
      color: theme.colors.textSecondary,
    },
    bookDescription: {
      color: theme.colors.text,
      marginBottom: 32,
      lineHeight: '1.6',
    },
    readButton: {
      maxWidth: 200,
    },
    readButtonLink: {
      textDecoration: 'none',
      display: 'block',
    },
    continueText: {
      color: theme.colors.accentYellow,
      marginBottom: 16,
    },
    similarSection: {
      marginTop: 60,
      paddingTop: 40,
      borderTop: `1px solid ${theme.colors.overlay}`,
    },
    sectionTitle: {
      color: theme.colors.text,
      marginBottom: 30,
      textAlign: 'center',
    },
    similarGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(${isLargeScreen ? '250px' : '170px'}, 1fr))`,
      gap: 20,
      justifyItems: 'center',
    },
    similarCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
      maxWidth: isLargeScreen ? 250 : 170,
      textDecoration: 'none',
      display: 'block',
    },
    similarImage: {
      width: '100%',
      height: isLargeScreen ? 280 : 200,
      objectFit: 'cover',
      objectPosition: 'top',
    },
    similarInfo: {
      padding: 12,
      textAlign: 'center',
    },
    similarTitle: {
      color: theme.colors.text,
      marginBottom: 8,
    },
    similarAuthor: {
      color: theme.colors.textSecondary,
      marginBottom: 8,
    },
    similarStats: {
      display: 'flex',
      justifyContent: 'center',
      gap: 8,
    },
    similarStatsText: {
      color: theme.colors.textTertiary,
    },
    readingStats: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        ogType={seoData.ogType}
      />
      <div style={styles.container}>
        <Header
          isPremium={isUserPremium}
          isAuthenticated={isAuthenticated}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={coinBalance}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
        />

        <main style={styles.main}>
          <div style={styles.bookContent}>
            <div style={styles.bookImageSection}>
              {book.picture && (
                <img
                  src={book.picture}
                  alt={seoData.alt}
                  style={styles.bookImage}
                />
              )}
            </div>

            <div style={styles.bookDetails}>
              <FontText h1 style={styles.bookTitle}>
                {book.title}
              </FontText>

              {mainAuthor && (
                <FontText h4 style={styles.bookAuthor}>
                  {t('by_author', { author: mainAuthor.name })}
                </FontText>
              )}

              <div style={styles.bookStats}>
                <div style={styles.statItem}>
                  <FontText small style={styles.statText}>
                    {t('chapter_count', { number: book.chapter_count })}
                  </FontText>
                </div>
                <FontText small style={styles.statText}>
                  |
                </FontText>
                <div style={styles.readingStats}>
                  <ReadingIcon />
                  <FontText small style={styles.statText}>
                    {book.read_count.toLocaleString()}
                  </FontText>
                </div>
              </div>

              {genres.length > 0 && (
                <div style={styles.bookGenres}>
                  {genres.map((genre, index) => (
                    <div key={index} style={styles.genreTag}>
                      <FontText small style={styles.genreText}>
                        {genre.emoji} {genre.title}
                      </FontText>
                    </div>
                  ))}
                </div>
              )}

              {book.description && (
                <FontText normal style={styles.bookDescription}>
                  {book.description}
                </FontText>
              )}

              {book.lastChapterRead && isAuthenticated && (
                <FontText small style={styles.continueText}>
                  {t('continue_reading')}{' '}
                  {t('chapter_label', { number: book.lastChapterRead })}
                </FontText>
              )}

              <div style={styles.readButton}>
                <Link
                  to={`/books/${book.slug}/chapter/${book.lastChapterRead || 1}`}
                  style={styles.readButtonLink}
                  onClick={handleReadNow}
                >
                  <PrimaryButton>
                    <FontText>
                      {book.lastChapterRead && isAuthenticated
                        ? t('continue_reading')
                        : t('read_now')}
                    </FontText>
                  </PrimaryButton>
                </Link>
              </div>
            </div>
          </div>

          {similarBooks.length > 0 && (
            <div style={styles.similarSection}>
              <FontText h2 style={styles.sectionTitle}>
                {t('similar_stories')}
              </FontText>
              <div style={styles.similarGrid}>
                {similarBooks.map(similarBook => (
                  <Link
                    key={similarBook.id}
                    to={`/books/${similarBook.slug}`}
                    style={styles.similarCard}
                    onClick={() => handleSimilarBookPress(similarBook)}
                  >
                    {similarBook.picture && (
                      <img
                        src={similarBook.picture}
                        alt={similarBook.title}
                        style={styles.similarImage}
                      />
                    )}
                    <div style={styles.similarInfo}>
                      <FontText style={styles.similarTitle}>
                        {similarBook.title}
                      </FontText>
                      {similarBook.book_author[0]?.author && (
                        <FontText small style={styles.similarAuthor}>
                          {t('by_author', {
                            author: similarBook.book_author[0].author.name,
                          })}
                        </FontText>
                      )}
                      <div style={styles.similarStats}>
                        <FontText tiny style={styles.similarStatsText}>
                          {t('chapter_count', {
                            number: similarBook.chapter_count,
                          })}
                        </FontText>
                        <FontText tiny style={styles.similarStatsText}>
                          |
                        </FontText>
                        <div style={styles.readingStats}>
                          <ReadingIcon />
                          <FontText tiny style={styles.similarStatsText}>
                            {similarBook.read_count.toLocaleString()}
                          </FontText>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};
